import { useState } from "react"
import {
	IconAtom2Outline18 as AiIcon,
	IconUserArrowRightOutline18 as EscalatedIcon,
} from "nucleo-ui-outline-18"

import { FeedbackOriginTextRecord } from "@productlane/lib"
import { formatRelativeTime } from "@productlane/lib/src/date"
import {
	Avatar,
	Badge,
	Button,
	Loader,
	ScrollArea,
	ScrollViewport,
	Tooltip,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { reportingCardClassName } from "@/components/reporting/charts"
import { api } from "@/lib/api"
import { FeedbackOriginIconRecord } from "@/lib/feedback"
import { AiConversationDialog } from "./ai-conversation-dialog"

const REFETCH_INTERVAL_MS = 15_000
const PAGE_SIZE = 90

export function AIResponsesLivestream() {
	const [openSessionId, setOpenSessionId] = useState<string | null>(null)
	const [limit, setLimit] = useState(PAGE_SIZE)
	const query = api.analytics.aiResponsesLivestream.useQuery(
		{ limit },
		{ refetchInterval: REFETCH_INTERVAL_MS, keepPreviousData: true },
	)

	const entries = query.data?.entries ?? []

	return (
		<div className={`${reportingCardClassName} flex h-[36rem] flex-col`}>
			<AiConversationDialog
				sessionId={openSessionId}
				onClose={() => setOpenSessionId(null)}
			/>

			<div className="flex items-center gap-1.5 border-b border-gray-200 px-4 py-3 dark:border-gray-700/80">
				<EscalatedIcon className="h-3.5 w-3.5 flex-none text-red-500" />
				<h3 className="text-sm font-medium text-secondary">
					Escalated conversations
				</h3>
				<Badge variant="light-gray" size="xs" className="ml-auto">
					{entries.length}
				</Badge>
				<span className="flex h-1.5 w-1.5 flex-none rounded-full bg-green-500" />
			</div>

			{query.isLoading ? (
				<div className="flex flex-1 items-center justify-center">
					<Loader />
				</div>
			) : entries.length === 0 ? (
				<div className="flex flex-1 items-center justify-center px-4 text-xs text-tertiary">
					Nothing here yet
				</div>
			) : (
				<ScrollArea className="min-h-0 flex-1">
					<ScrollViewport>
						<div className="divide-y divide-gray-200 pb-2 dark:divide-gray-700/80">
							{entries.map((entry) => (
								<LivestreamItem
									key={entry.id}
									entry={entry}
									onSelect={setOpenSessionId}
								/>
							))}
						</div>
					</ScrollViewport>
				</ScrollArea>
			)}

			{query.data?.hasMore ? (
				<div className="flex flex-none justify-center border-t border-gray-200 px-4 py-2 dark:border-gray-700/80">
					<Button
						variant="ghost"
						size="sm"
						isLoading={query.isPreviousData}
						onClick={() => setLimit((current) => current + PAGE_SIZE)}
					>
						Load more
					</Button>
				</div>
			) : null}
		</div>
	)
}

type LivestreamEntry =
	RouterOutputs["analytics"]["aiResponsesLivestream"]["entries"][number]

function LivestreamItem({
	entry,
	onSelect,
}: {
	entry: LivestreamEntry
	onSelect: (sessionId: string) => void
}) {
	const OriginIcon = entry.threadOrigin
		? FeedbackOriginIconRecord[entry.threadOrigin]
		: null

	return (
		<button
			type="button"
			onClick={() => onSelect(entry.sessionId)}
			className="block w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-850/50"
		>
			<div className="flex items-center gap-1.5">
				<Avatar
					size="xs"
					name={entry.workspaceName}
					image={entry.workspaceLogoUrl}
				/>
				<span className="truncate text-[11px] font-medium text-secondary">
					{entry.workspaceName}
				</span>
				{OriginIcon && entry.threadOrigin ? (
					<Tooltip label={FeedbackOriginTextRecord[entry.threadOrigin]}>
						<span className="inline-flex">
							<OriginIcon className="h-3 w-3 flex-none text-tertiary" />
						</span>
					</Tooltip>
				) : null}
				<span className="ml-auto shrink-0 text-[11px] text-tertiary">
					{formatRelativeTime(entry.createdAt)}
				</span>
			</div>

			<div className="mt-2 flex flex-col gap-1.5">
				{entry.question ? (
					<div className="max-w-[88%] self-start rounded-[10px] bg-gray-100 px-2.5 py-2 dark:bg-gray-800">
						<p className="line-clamp-3 text-[13px] font-medium leading-snug text-primary">
							{entry.question}
						</p>
					</div>
				) : null}
				<div className="bg-indigo-50 dark:bg-indigo-500/10 max-w-[88%] self-end rounded-[10px] px-2.5 py-2">
					<div className="mb-0.5 flex items-center gap-1 text-[10.5px] font-medium text-tertiary">
						<AiIcon className="h-3 w-3 flex-none" />
						AI agent
					</div>
					<p className="line-clamp-3 whitespace-pre-line text-[13px] leading-snug text-secondary">
						{entry.answer}
					</p>
				</div>
			</div>
		</button>
	)
}
